// pages/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../services/api';
import { toast } from 'react-toastify';
import { useUserInfo, useShowRegisterModal, useImpersonatedUserId, useAdminUsers, usePersonChanged } from '../contexts/GlobalStateContext';
import RegisterForm from './RegisterForm';
import BattenburgLoader from '../common/BattenburgLoader';
import InitialStateService from '../services/InitialStateService';
import StationUpdateForm from './StationUpdateForm';
import 'react-toastify/dist/ReactToastify.css';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState(new Set());
  const [selectedUser, setSelectedUser] = useState(null);
  const [initialStationName, setInitialStationName] = useState(null);
  const [loading, setLoading] = useState(true);

  const { userInfo } = useUserInfo();
  const { personChanged } = usePersonChanged();
  const { showRegisterModal, setShowRegisterModal } = useShowRegisterModal();
  const { adminUsers, setAdminUsers } = useAdminUsers();
  const { impersonatedUserId, setImpersonatedUserId } = useImpersonatedUserId();

  const isSystemAdmin = userInfo?.role === 'SystemAdmin';

  // Hämta alla användare
  const loadUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/users');
      if (!response.ok) throw new Error('Kunde inte ladda användare');
      const data = await response.json();
      setUsers(data);
      const onlineIds = new Set(data.filter(u => u.isOnline).map(u => u.userId));
      setActiveUsers(onlineIds);
    } catch (err) {
      toast.error('❌ Kunde inte ladda användare');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/users');
      if (!response.ok) return;
      const data = await response.json();
      setUsers(data);
      setActiveUsers(new Set(data.filter(u => u.isOnline).map(u => u.userId)));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUserRoleAndAdmins = async () => {
    if (!userInfo) return;
    try {
      if (userInfo.role === 'SystemAdmin') {
        const adminResp = await fetchWithAuth('/api/admin/admins');
        if (!adminResp.ok) throw new Error('Kunde inte hämta adminlista');
        const admins = await adminResp.json();
        setAdminUsers(admins);
      }
    } catch (err) {
      toast.error(`Fel vid hämtning av roller/admins: ${err.message}`);
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isSystemAdmin) return;
    loadUsers();
    fetchUserRoleAndAdmins();
    const interval = setInterval(fetchActiveUsers, 30000);
    window.addEventListener('focus', fetchActiveUsers);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', fetchActiveUsers);
    };
  }, [userInfo, personChanged]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      const resp = await fetchWithAuth(`/api/admin/users/${userId}/role`, {
        method: 'POST',
        body: JSON.stringify({ newRole })
      });
      if (resp.ok) {
        toast.success(`✅ Roll uppdaterad till ${newRole}!`);
        loadUsers();
        fetchUserRoleAndAdmins();
      } else {
        const error = await resp.json();
        toast.error(`❌ ${error.message || 'Kunde inte uppdatera roll'}`);
      }
    } catch (err) {
      toast.error('❌ Fel vid uppdatering av roll');
      console.error(err);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Är du säker på att ta bort användaren "${username}"?`)) return;
    try {
      const resp = await fetchWithAuth(`/api/admin/users/${userId}`, { method: 'DELETE' });
      if (resp.ok) {
        toast.success(`✅ Användare "${username}" borttagen!`);
        loadUsers();
        fetchUserRoleAndAdmins();
      } else {
        const error = await resp.json();
        toast.error(`❌ ${error.message || 'Kunde inte ta bort användare'}`);
      }
    } catch (err) {
      toast.error('❌ Fel vid borttagning av användare');
      console.error(err);
    }
  };

  if (!isSystemAdmin) {
    return (
      <div className="tw-p-6 tw-bg-red-50 tw-rounded-lg tw-mt-10 tw-max-w-2xl tw-mx-auto">
        <h4 className="tw-text-red-800 tw-font-bold">Åtkomst nekad</h4>
        <p className="tw-text-red-700">Du har inte behörighet att se denna sida.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-min-h-[200px]">
        <BattenburgLoader />
      </div>
    );
  }

  // Statistikräknare
  const roleCounts = {
    SystemAdmin: users.filter(u => u.roleName === 'SystemAdmin').length,
    Admin: users.filter(u => u.roleName === 'Admin').length,
    Standard: users.filter(u => u.roleName === 'Standard').length,
  };

  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-max-w-6xl tw-mx-auto">
        {/* Header */}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6 tw-flex-wrap tw-gap-4">
          <h2 className="tw-text-2xl tw-font-semibold tw-flex tw-items-center">
            <i className="fas fa-users-cog tw-mr-2"></i> Användarhantering
          </h2>

          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <div className="tw-bg-green-100 tw-text-green-800 tw-px-3 tw-py-1 tw-rounded-full tw-flex tw-items-center tw-gap-1">
              <i className="fas fa-circle tw-text-green-600 tw-text-[10px] tw-animate-pulse"></i>
              {activeUsers.size} online
            </div>
            <div className="tw-bg-blue-100 tw-text-blue-800 tw-px-3 tw-py-1 tw-rounded-full tw-flex tw-items-center tw-gap-1">
              <i className="fas fa-users"></i> {users.length} totalt
            </div>
            <button
              onClick={() => setShowRegisterModal(true)}
              className="tw-bg-green-600 hover:tw-bg-green-700 tw-text-white tw-px-3 tw-py-1 tw-rounded-lg tw-flex tw-items-center tw-gap-2"
              title="Registrera ny användare"
            >
              <i className="fas fa-user-plus"></i> Registrera
            </button>
          </div>
        </div>

        {/* Rollförklaring */}
        <div className="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-mb-6 tw-text-blue-800">
          <h5 className="tw-font-semibold tw-flex tw-items-center tw-mb-2">
            <i className="fas fa-info-circle tw-mr-2"></i> Roller och behörigheter
          </h5>
          <ul className="tw-list-disc tw-ml-5">
            <li><strong>SystemAdmin:</strong> Full åtkomst, kan hantera användare</li>
            <li><strong>Admin:</strong> Stationsansvarig, kan importera data och skapa scheman</li>
            <li><strong>Standard:</strong> Kan endast se scheman och välja station</li>
          </ul>
        </div>

        {/* Impersonering */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-6">
          <h2 className="tw-text-2xl tw-font-semibold tw-text-gray-700">Välj användare att agera som:&nbsp;</h2>
          <select
            value={impersonatedUserId || ""}
            onChange={async (e) => {
              const val = e.target.value;
              if (val === "none") {
                setImpersonatedUserId("");
                await InitialStateService.getInitialState("");
                return;
              }
              setImpersonatedUserId(val);
              await InitialStateService.getInitialState(val);
            }}
            className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-2 tw-py-1 tw-bg-white hover:tw-border-gray-400 focus:tw-outline-none"
          >
            <option value="">-- Välj användare --</option>
            <option value="none">Ingen</option>
            {adminUsers.map(a => (
              <option key={a.id} value={a.id}>{a.username} {a.station ? `(${a.station})` : ""}</option>
            ))}
          </select>
        </div>

        {/* Användartabell */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-overflow-auto">
          <table className="tw-min-w-full tw-table-auto">
            <thead className="tw-bg-gray-100 tw-text-gray-700">
              <tr>
                <th className="tw-py-2 tw-px-3 tw-w-[5%]"></th>
                <th className="tw-py-2 tw-px-3 tw-text-left">Användarnamn</th>
                <th className="tw-py-2 tw-px-3 tw-text-left">Roll</th>
                <th className="tw-py-2 tw-px-3 tw-text-left">Editera Stationslista</th>
                <th className="tw-py-2 tw-px-3 tw-text-center">Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.userId} className="tw-border-b tw-border-gray-200">
                  <td className="tw-text-center tw-py-2">
                    {user.isOnline ? (
                      <i className="fas fa-circle tw-text-green-600 tw-text-[10px] tw-animate-pulse" title="Online nu"></i>
                    ) : (
                      <i className="fas fa-circle tw-text-gray-400 tw-text-[10px]" title="Offline"></i>
                    )}
                  </td>
                  <td className="tw-py-2">
                    <strong>{user.userName}</strong>
                    {user.userName === 'SystemAdmin' && <span className="tw-ml-2 tw-px-2 tw-py-1 tw-bg-red-600 tw-text-white tw-rounded-full tw-text-xs">System</span>}
                    {user.isOnline && user.lastActivitySweden && (
                      <small className="tw-block tw-text-gray-500">Aktiv {new Date(user.lastActivitySweden).toLocaleTimeString('sv-SE')}</small>
                    )}
                  </td>
                  <td className="tw-py-2">
                    <select
                      value={user.roleName}
                      onChange={(e) => handleRoleChange(user.userId, e.target.value)}
                      disabled={user.userName === 'SystemAdmin' || user.userName === "PublicViewer" || user.userName === "PublicLinkViewer"}
                      className={`tw-px-2 tw-py-1 tw-rounded-lg tw-text-white ${user.roleName === 'SystemAdmin' ? 'tw-bg-red-600' :
                        user.roleName === 'Admin' ? 'tw-bg-blue-600' :
                          'tw-bg-gray-600'
                        }`}
                    >
                      {user.userName === 'SystemAdmin' && <option value="SystemAdmin">SystemAdmin</option>}
                      <option value="Standard">Standard</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </td>
                  <td className="tw-py-2 tw-text-center">
                    {user.userName !== 'SystemAdmin' && user.userName !== 'PublicViewer' && user.userName !== "PublicLinkViewer" && (
                      <button
                        onClick={async () => {
                          setSelectedUser(user);
                          const initialState = await InitialStateService.getInitialState(user.userId);
                          setInitialStationName(initialState.station);
                        }}
                        className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-px-2 tw-py-1 tw-rounded-lg"
                        title="Uppdatera stationslista"
                      >
                        <i className="fas fa-pen tw-mr-1"></i> Uppdatera
                      </button>
                    )}
                  </td>
                  <td className="tw-py-2 tw-text-center">
                    {(user.userName === 'SystemAdmin' || user.userName === "PublicViewer" || user.userName === "PublicLinkViewer") ? (
                      <span className="tw-text-gray-400 tw-italic">Kan ej tas bort</span>
                    ) : (
                      <button
                        onClick={() => handleDeleteUser(user.userId, user.userName)}
                        className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-px-2 tw-py-1 tw-rounded-lg"
                        title="Ta bort användare"
                      >
                        <i className="fas fa-trash tw-mr-1"></i> Ta bort
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Statistikkort */}
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4 tw-mt-6">
          {['SystemAdmin', 'Admin', 'Standard'].map(role => (
            <div
              key={role}
              className={`tw-p-4 tw-rounded-lg tw-shadow tw-flex tw-flex-col tw-items-center tw-justify-center ${role === 'SystemAdmin' ? 'tw-bg-red-600 tw-text-white' :
                  role === 'Admin' ? 'tw-bg-blue-600 tw-text-white' :
                    'tw-bg-gray-600 tw-text-white'
                }`}
            >
              <h5 className="tw-font-semibold">{role}</h5>
              <h2 className="tw-text-2xl tw-font-bold">{roleCounts[role]}</h2>
            </div>
          ))}
        </div>

        {/* Modals */}
        {showRegisterModal && (
          <RegisterForm
            onClose={() => loadUsers()}
          />
        )}
        {selectedUser && initialStationName && (
          <StationUpdateForm
            user={selectedUser}
            onClose={() => {
              setSelectedUser(null);
              setInitialStationName(null);
              loadUsers();
            }}
            initialStationName={initialStationName}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
